import { useRef, useCallback, useEffect } from "react";

export type OscWaveform = "sawtooth" | "square" | "sine" | "triangle";
export type LfoDest = "filter" | "pitch" | "amp";

export interface RS1State {
  waveform1: OscWaveform;
  waveform2: OscWaveform;
  mix: number;
  detune: number;
  cutoff: number;
  resonance: number;
  filterEnvAmt: number;
  attack: number;
  decay: number;
  sustain: number;
  release: number;
  fAttack: number;
  fDecay: number;
  fSustain: number;
  fRelease: number;
  lfoWaveform: OscWaveform;
  lfoRate: number;
  lfoDepth: number;
  lfoDest: LfoDest;
  drive: number;
  delayTime: number;
  delayMix: number;
  reverbSize: number;
  reverbMix: number;
  volume: number;
}

interface Voice {
  osc1: OscillatorNode;
  osc2: OscillatorNode;
  osc1Gain: GainNode;
  osc2Gain: GainNode;
  filter: BiquadFilterNode;
  gain: GainNode;
  lfoTargetGain: GainNode;
}

interface AudioNodes {
  masterGain: GainNode | null;
  compressor: DynamicsCompressorNode | null;
  driveNode: WaveShaperNode | null;
  delayNode: DelayNode | null;
  delayFeedback: GainNode | null;
  delayDry: GainNode | null;
  delayWet: GainNode | null;
  reverbNode: ConvolverNode | null;
  reverbDry: GainNode | null;
  reverbWet: GainNode | null;
  lfoNode: OscillatorNode | null;
  lfoGain: GainNode | null;
  analyser: AnalyserNode | null;
}

const NOTE_FREQS: Record<string, number> = {
  C: 261.63, "C#": 277.18, D: 293.66, "D#": 311.13,
  E: 329.63, F: 349.23, "F#": 369.99, G: 392.0,
  "G#": 415.3, A: 440.0, "A#": 466.16, B: 493.88,
  C2: 523.25, "C#2": 554.37, D2: 587.33, "D#2": 622.25,
  E2: 659.26, F2: 698.46,
};

function createDriveCurve(amount: number): Float32Array {
  const samples = 44100;
  const curve = new Float32Array(samples);
  const k = amount * 50;
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = ((3 + k) * x * 20 * (Math.PI / 180)) / (Math.PI + k * Math.abs(x));
  }
  return curve;
}

function createReverbIR(ctx: AudioContext, duration: number, decay: number): AudioBuffer {
  const sampleRate = ctx.sampleRate;
  const length = sampleRate * duration;
  const impulse = ctx.createBuffer(2, length, sampleRate);
  for (let channel = 0; channel < 2; channel++) {
    const channelData = impulse.getChannelData(channel);
    for (let i = 0; i < length; i++) {
      channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, decay);
    }
  }
  return impulse;
}

export function useRS1Audio(state: RS1State) {
  const ctxRef = useRef<AudioContext | null>(null);
  const nodesRef = useRef<AudioNodes>({
    masterGain: null,
    compressor: null,
    driveNode: null,
    delayNode: null,
    delayFeedback: null,
    delayDry: null,
    delayWet: null,
    reverbNode: null,
    reverbDry: null,
    reverbWet: null,
    lfoNode: null,
    lfoGain: null,
    analyser: null,
  });
  const activeNotesRef = useRef<Map<string, Voice>>(new Map());
  const stateRef = useRef(state);

  // Keep stateRef in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const init = useCallback(() => {
    if (ctxRef.current) {
      // Resume if suspended (needed after user gesture)
      if (ctxRef.current.state === "suspended") {
        ctxRef.current.resume();
      }
      return ctxRef.current;
    }

    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    ctxRef.current = ctx;
    // Try to resume immediately (will work if called from user gesture)
    if (ctx.state === "suspended") {
      ctx.resume();
    }
    const nodes = nodesRef.current;
    const s = stateRef.current;

    // Compressor
    nodes.compressor = ctx.createDynamicsCompressor();
    nodes.compressor.threshold.value = -24;
    nodes.compressor.knee.value = 30;
    nodes.compressor.ratio.value = 12;
    nodes.compressor.attack.value = 0.003;
    nodes.compressor.release.value = 0.25;

    // Master gain
    nodes.masterGain = ctx.createGain();
    nodes.masterGain.gain.value = s.volume;

    // Drive
    nodes.driveNode = ctx.createWaveShaper();
    nodes.driveNode.curve = createDriveCurve(s.drive);
    nodes.driveNode.oversample = "2x";

    // Delay
    nodes.delayNode = ctx.createDelay(2.0);
    nodes.delayNode.delayTime.value = s.delayTime;
    nodes.delayFeedback = ctx.createGain();
    nodes.delayFeedback.gain.value = 0.4;
    nodes.delayDry = ctx.createGain();
    nodes.delayDry.gain.value = 1;
    nodes.delayWet = ctx.createGain();
    nodes.delayWet.gain.value = s.delayMix;

    // Reverb
    nodes.reverbNode = ctx.createConvolver();
    nodes.reverbNode.buffer = createReverbIR(ctx, 2 + s.reverbSize * 3, 2 + s.reverbSize * 4);
    nodes.reverbDry = ctx.createGain();
    nodes.reverbDry.gain.value = 1;
    nodes.reverbWet = ctx.createGain();
    nodes.reverbWet.gain.value = s.reverbMix;

    // LFO
    nodes.lfoNode = ctx.createOscillator();
    nodes.lfoNode.type = s.lfoWaveform;
    nodes.lfoNode.frequency.value = s.lfoRate * 20;
    nodes.lfoGain = ctx.createGain();
    nodes.lfoGain.gain.value = 0;
    nodes.lfoNode.connect(nodes.lfoGain);
    nodes.lfoNode.start();

    // Analyser
    nodes.analyser = ctx.createAnalyser();
    nodes.analyser.fftSize = 2048;
    nodes.analyser.smoothingTimeConstant = 0.8;

    // Signal chain
    nodes.masterGain.connect(nodes.driveNode);
    nodes.driveNode.connect(nodes.delayDry);
    nodes.driveNode.connect(nodes.delayNode);
    nodes.delayNode.connect(nodes.delayFeedback);
    nodes.delayFeedback.connect(nodes.delayNode);
    nodes.delayNode.connect(nodes.delayWet);

    const delayMix = ctx.createGain();
    nodes.delayDry.connect(delayMix);
    nodes.delayWet.connect(delayMix);

    delayMix.connect(nodes.reverbDry);
    delayMix.connect(nodes.reverbNode);
    nodes.reverbNode.connect(nodes.reverbWet);

    nodes.reverbDry.connect(nodes.compressor);
    nodes.reverbWet.connect(nodes.compressor);

    nodes.compressor.connect(nodes.analyser);
    nodes.analyser.connect(ctx.destination);

    return ctx;
  }, []);

  const getFrequency = useCallback((note: string, octave: number): number => {
    const baseFreq = NOTE_FREQS[note];
    if (!baseFreq) return 440;
    return baseFreq * Math.pow(2, octave - 4);
  }, []);

  const noteOn = useCallback((note: string, octave: number, velocity = 127) => {
    if (activeNotesRef.current.has(note)) return;

    const ctx = init();
    if (ctx.state === "suspended") ctx.resume();

    const nodes = nodesRef.current;
    const s = stateRef.current;
    const freq = getFrequency(note, octave);
    const now = ctx.currentTime;

    // OSC 1
    const osc1 = ctx.createOscillator();
    osc1.type = s.waveform1;
    osc1.frequency.value = freq;

    // OSC 2
    const osc2 = ctx.createOscillator();
    osc2.type = s.waveform2;
    osc2.frequency.value = freq;
    osc2.detune.value = s.detune;

    // Mixer
    const osc1Gain = ctx.createGain();
    const osc2Gain = ctx.createGain();
    osc1Gain.gain.value = 1 - s.mix;
    osc2Gain.gain.value = s.mix;

    // Filter
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    const baseCutoff = 20000 * s.cutoff;
    filter.frequency.value = baseCutoff;
    filter.Q.value = s.resonance * 20;

    // Filter envelope
    const envRange = 18000 * s.filterEnvAmt;
    const startFreq = Math.max(20, baseCutoff - envRange);
    const peakFreq = Math.min(20000, baseCutoff + envRange);
    const sustainFreq = startFreq + (peakFreq - startFreq) * s.fSustain;

    filter.frequency.setValueAtTime(startFreq, now);
    filter.frequency.linearRampToValueAtTime(peakFreq, now + s.fAttack);
    filter.frequency.linearRampToValueAtTime(sustainFreq, now + s.fAttack + s.fDecay);

    // Amp envelope - scale by velocity (0-127 -> 0-1)
    const velocityScale = velocity / 127;
    const voiceVol = 0.4 * velocityScale;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(voiceVol, now + s.attack);
    gain.gain.linearRampToValueAtTime(s.sustain * voiceVol, now + s.attack + s.decay);

    // Connect
    osc1.connect(osc1Gain);
    osc2.connect(osc2Gain);
    osc1Gain.connect(filter);
    osc2Gain.connect(filter);
    filter.connect(gain);
    gain.connect(nodes.masterGain!);

    // LFO routing
    const lfoTargetGain = ctx.createGain();
    nodes.lfoGain!.connect(lfoTargetGain);

    if (s.lfoDest === "filter") {
      lfoTargetGain.gain.value = s.lfoDepth * 5000;
      lfoTargetGain.connect(filter.frequency);
    } else if (s.lfoDest === "pitch") {
      lfoTargetGain.gain.value = s.lfoDepth * 50;
      lfoTargetGain.connect(osc1.frequency);
      lfoTargetGain.connect(osc2.frequency);
    } else if (s.lfoDest === "amp") {
      lfoTargetGain.gain.value = s.lfoDepth * 0.3;
      lfoTargetGain.connect(gain.gain);
    }

    osc1.start(now);
    osc2.start(now);

    activeNotesRef.current.set(note, {
      osc1, osc2, osc1Gain, osc2Gain, filter, gain, lfoTargetGain,
    });
  }, [init, getFrequency]);

  const noteOff = useCallback((note: string) => {
    const voice = activeNotesRef.current.get(note);
    if (!voice || !ctxRef.current) return;

    const ctx = ctxRef.current;
    const s = stateRef.current;
    const now = ctx.currentTime;

    voice.gain.gain.cancelScheduledValues(now);
    const currentGain = voice.gain.gain.value;
    const minRelease = 0.08;
    const releaseTime = Math.max(minRelease, s.release);
    const timeConstant = releaseTime / 5;

    voice.gain.gain.setValueAtTime(Math.max(currentGain, 0.001), now);
    voice.gain.gain.setTargetAtTime(0.0001, now, timeConstant);

    voice.filter.frequency.cancelScheduledValues(now);
    const currentFreq = Math.max(voice.filter.frequency.value, 20);
    const fReleaseTime = Math.max(minRelease, s.fRelease);
    voice.filter.frequency.setValueAtTime(currentFreq, now);
    voice.filter.frequency.setTargetAtTime(20, now, fReleaseTime / 5);

    voice.osc1.stop(now + releaseTime + 0.5);
    voice.osc2.stop(now + releaseTime + 0.5);

    activeNotesRef.current.delete(note);
  }, []);

  const updateParam = useCallback((param: keyof RS1State, value: number) => {
    const nodes = nodesRef.current;
    const ctx = ctxRef.current;

    if (param === "volume" && nodes.masterGain) {
      nodes.masterGain.gain.value = value;
    }
    if (param === "drive" && nodes.driveNode) {
      nodes.driveNode.curve = createDriveCurve(value);
    }
    if (param === "delayTime" && nodes.delayNode) {
      nodes.delayNode.delayTime.value = value;
    }
    if (param === "delayMix" && nodes.delayWet) {
      nodes.delayWet.gain.value = value;
    }
    if (param === "reverbMix" && nodes.reverbWet) {
      nodes.reverbWet.gain.value = value;
    }
    if (param === "reverbSize" && nodes.reverbNode && ctx) {
      nodes.reverbNode.buffer = createReverbIR(ctx, 2 + value * 3, 2 + value * 4);
    }
    if (param === "lfoRate" && nodes.lfoNode) {
      nodes.lfoNode.frequency.value = value * 20;
    }
    if (param === "lfoDepth" && nodes.lfoGain) {
      nodes.lfoGain.gain.value = value;
    }

    // Update active voices
    activeNotesRef.current.forEach((voice) => {
      if (param === "cutoff") {
        voice.filter.frequency.value = 20000 * value;
      }
      if (param === "resonance") {
        voice.filter.Q.value = value * 20;
      }
      if (param === "mix") {
        voice.osc1Gain.gain.value = 1 - value;
        voice.osc2Gain.gain.value = value;
      }
      if (param === "detune") {
        voice.osc2.detune.value = value;
      }
    });
  }, []);

  const setLfoWaveform = useCallback((waveform: OscWaveform) => {
    if (nodesRef.current.lfoNode) {
      nodesRef.current.lfoNode.type = waveform;
    }
  }, []);

  const setOscWaveform = useCallback((osc: 1 | 2, waveform: OscWaveform) => {
    activeNotesRef.current.forEach((voice) => {
      if (osc === 1) voice.osc1.type = waveform;
      else voice.osc2.type = waveform;
    });
  }, []);

  const getAnalyserData = useCallback((): Uint8Array | null => {
    if (!nodesRef.current.analyser) return null;
    const data = new Uint8Array(nodesRef.current.analyser.fftSize);
    nodesRef.current.analyser.getByteTimeDomainData(data);
    return data;
  }, []);

  const hasActiveNotes = useCallback((): boolean => {
    return activeNotesRef.current.size > 0;
  }, []);

  // Resume audio context when page becomes visible (after phone lock/unlock)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && ctxRef.current?.state === "suspended") {
        ctxRef.current.resume();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      ctxRef.current?.close();
    };
  }, []);

  return {
    noteOn,
    noteOff,
    updateParam,
    setLfoWaveform,
    setOscWaveform,
    getAnalyserData,
    hasActiveNotes,
    init,
  };
}
